import { useState } from "react";
import SEO from "@/components/SEO";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Send } from "lucide-react";

const services = [
  "SSL Installation",
  "Website Security Audit",
  "Premium Website Security",
  "Enterprise Security",
  "Website Design",
  "Web Hosting",
  "Domain Registration",
  "Technical Support",
  "Custom Enterprise Solution",
];

export default function QuoteRequest() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    service: "SSL Installation", budget: "", timeline: "", details: "",
  });
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/quote", form);
      nav("/quote-success");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Request a Quote — Tech Alpha SSL" description="Request a tailored quote for SSL, security, hosting, or design services." canonical="https://techalphassl.com/quote" />
      <section className="pt-20 pb-10">
        <div className="container-page max-w-3xl">
          <span className="eyebrow">Request a quote</span>
          <h1 className="mt-4 text-4xl md:text-6xl font-semibold tracking-tighter">Tell us about your project.</h1>
          <p className="mt-4 text-lg text-muted-foreground">A senior engineer will reach out within one business day.</p>
        </div>
      </section>

      <section className="pb-20">
        <div className="container-page max-w-3xl">
          <form onSubmit={submit} className="card-elevated p-8 space-y-4" data-testid="quote-form">
            <div className="grid gap-4 md:grid-cols-2">
              <Input label="Name" required value={form.name} onChange={upd("name")} testId="quote-name" />
              <Input label="Email" required type="email" value={form.email} onChange={upd("email")} testId="quote-email" />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Input label="Company" value={form.company} onChange={upd("company")} testId="quote-company" />
              <Input label="Phone" value={form.phone} onChange={upd("phone")} testId="quote-phone" />
            </div>
            <div>
              <label className="text-sm font-medium">Service *</label>
              <select
                required
                value={form.service}
                onChange={upd("service")}
                data-testid="quote-service"
                className="mt-1 w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm outline-none focus:border-[#1565FF]"
              >
                {services.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Input label="Budget (optional)" placeholder="$1,000–$5,000" value={form.budget} onChange={upd("budget")} testId="quote-budget" />
              <Input label="Timeline (optional)" placeholder="Within 30 days" value={form.timeline} onChange={upd("timeline")} testId="quote-timeline" />
            </div>
            <div>
              <label className="text-sm font-medium">Project details *</label>
              <textarea
                required rows={6} value={form.details} onChange={upd("details")} data-testid="quote-details"
                className="mt-1 w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm outline-none focus:border-[#1565FF]"
                placeholder="Tell us about your site, stack, and what you're trying to solve."
              />
            </div>
            <button type="submit" disabled={loading} className="btn-primary" data-testid="quote-submit">
              {loading ? "Submitting..." : "Submit request"} <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      </section>
    </>
  );
}

function Input({ label, required, type = "text", value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}{required && " *"}</label>
      <input
        required={required} type={type} value={value} onChange={onChange} placeholder={placeholder}
        data-testid={testId}
        className="mt-1 w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm outline-none focus:border-[#1565FF]"
      />
    </div>
  );
}
